import React, { useState } from 'react';
import SanctionsioScreeningCard from './SanctionsioScreeningCard';
import DetailDrawer from '../../../common/DetailDrawer';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { createKycNestedSanctionComment, amlWatcherHits } from '../../../../API/Api';
import { toast } from 'react-toastify';

/**
 * ACRA AML (amlwatcher) standalone SanctionsScreeningSection.
 * Always uses amlWatcher API; does not depend on MAS_AML components.
 */
const SanctionsScreeningSection = ({
  userId,
  decodedToken,
  readOnly,
  setFormData,
  qnCounter,
  formData,
  kycData,
  crimesHitsLoading,
  checkRiskStatus,
  apiFailed,
}) => {
  const [assessmentRunning, setAssessmentRunning] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ isOpen: false, summary: null });

  const openDetailDrawer = summary => {
    setDetailDrawer({ isOpen: true, summary });
  };

  const closeDetailDrawer = () => {
    setDetailDrawer({ isOpen: false, summary: null });
  };

  const handleSubmitNestedArrayComment = async (e, formName, nestedName, index) => {
    if (formData[formName][index][nestedName].trim() === '') {
      return;
    }

    const newCommentEntry = {
      screenedDate: formData.screenedDate,
      kycaml_client_id: userId,
      question: formName + ' ' + index.toString(),
      content: formData[formName][index][nestedName],
      name: decodedToken.name,
      commented_at: DateTime.now().setZone('Asia/Singapore').toFormat('dd-MM-yyyy, HH:mm:ss'),
      comment_id: uuidv4(),
    };
    await createKycNestedSanctionComment(newCommentEntry);
    formData[formName][index].comments.push(newCommentEntry);
    const newCommentArray = formData[formName][index].comments;

    const newPepArray = [...formData[formName]];
    newPepArray[index].comments = newCommentArray;
    newPepArray[index].newComment = '';

    setFormData(prevState => ({
      ...prevState,
      [formName]: newPepArray,
    }));
  };

  const handleNestedArrayInputChange = (e, nestedName, index) => {
    let { name, value } = e.target;
    name = name.split(' ')[0];
    const newArrayResult = [...formData[name]];
    newArrayResult[index][nestedName] = value;

    const newFormData = {
      ...formData,
      [name]: newArrayResult,
    };
    setFormData(newFormData);
    checkRiskStatus(newFormData, undefined, false);
  };

  const handleRunAssessment = async screeningSource => {
    setAssessmentRunning(true);
    toast.info(`Assessment for ${screeningSource} running... `);

    try {
      const screenedName = kycData.name;
      const entityType = kycData.type.toUpperCase();

      const payload = {
        screenedName,
        connectedParty: kycData.details?.connectedParty,
        entityType: entityType,
        kycaml_client_id: kycData['_id'],
        screenedDate: formData['screenedDate'],
        screeningSource: 'amlwatcher',
      };

      const amlwatcherResponse = await amlWatcherHits(payload);

      if (amlwatcherResponse.data.message === 'ERR_BAD_REQUEST') {
        toast.error('Invalid amlwatcher API key/secret!');
      } else {
        const { pepHits, crimesHits, sanctionsHits, highRiskJurisdictionHits, adverseNewsHits, questionApiUrl } =
          amlwatcherResponse.data;
        setFormData({
          ...formData,
          pepHits,
          crimesHits,
          sanctionsHits,
          highRiskJurisdictionHits,
          adverseNewsHits,
          screeningSource: 'amlWatcher',
          questionApiUrl,
        });
        toast.success(`Assessment complete!`);
      }
    } catch (e) {
      console.log(e);
      toast.error('Something went wrong...');
    }

    setAssessmentRunning(false);
  };

  const rcaTooltip =
    "RCA (Relatives and Close Associates) - Immediate family members or close social or professional contacts of a government or political official, or senior executive – meaning spouses, parents, siblings, children, and spouses' parents and siblings.";

  return (
    <div>
      <DetailDrawer isOpen={detailDrawer.isOpen} onClose={closeDetailDrawer} summary={detailDrawer.summary} />

      <SanctionsioScreeningCard
        disabled={readOnly}
        questionNumber={qnCounter++}
        handleSubmitComment={handleSubmitNestedArrayComment}
        result={formData}
        inputName={'pepHits'}
        pepExtra={kycData}
        handleInputChange={handleNestedArrayInputChange}
        question={'Were there any hits for PEP screening?'}
        onOpenDetail={openDetailDrawer}
        apiFailed={apiFailed}
        tableHeaders={[
          {
            displayTitle: 'Name/Summary',
            width: 'w-3/12',
            dataField: result => (
              <div className='relative flex items-center space-x-2 group'>
                <button
                  className='text-blue-500 hover:text-blue-700 mr-1'
                  onClick={e => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title='View details'
                >
                  ℹ️
                </button>
                <span>{`${result?.name} - \n ${result?.title ? result?.title : result.summary?.notes ? [0] : ''}`}</span>
                {result?.matched_rca && (
                  <div className='relative'>
                    <span className='inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded'>
                      RCA
                    </span>
                    <div className='absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64'>
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          {
            displayTitle: 'Data Source',
            width: 'w-1.5/12',
            dataField: result => (
              <>
                {result?.dataSources?.length > 0 ? (
                  result?.dataSources.map((dataSource, index) => (
                    <div key={index}>
                      {dataSource?.url ? (
                        <span>
                          {dataSource?.publisher}
                          <a className='text-blue-500' href={dataSource.url} target='_blank' rel='noopener noreferrer'>
                            <svg
                              className='w-4 h-4 inline ml-1'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                              />
                            </svg>
                          </a>
                        </span>
                      ) : (
                        <span>{dataSource?.publisher} N/A</span>
                      )}
                    </div>
                  ))
                ) : (
                  <span>N/A</span>
                )}
              </>
            ),
          },
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      />

      <SanctionsioScreeningCard
        disabled={readOnly}
        questionNumber={qnCounter++}
        handleSubmitComment={handleSubmitNestedArrayComment}
        result={formData}
        inputName={'crimesHits'}
        handleInputChange={handleNestedArrayInputChange}
        question={'Were there any hits for crime?'}
        onOpenDetail={openDetailDrawer}
        loading={crimesHitsLoading}
        apiFailed={apiFailed}
        tableHeaders={[
          {
            displayTitle: 'Name / DataSource',
            width: 'w-2/12',
            dataField: result => (
              <div className='relative flex items-center space-x-2 group'>
                <button
                  className='text-blue-500 hover:text-blue-700 mr-1'
                  onClick={e => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title='View details'
                >
                  ℹ️
                </button>
                <span>{`${result.name} \n `}</span>
                {result?.matched_rca && (
                  <div className='relative'>
                    <span className='inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded'>
                      RCA
                    </span>
                    <div className='absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64'>
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          {
            displayTitle: 'Data Source',
            width: 'w-1.5/12',
            dataField: result => (
              <>
                {result?.dataSources?.length > 0 ? (
                  result?.dataSources.map((dataSource, index) => (
                    <div key={index}>
                      {dataSource?.url ? (
                        <span>
                          {dataSource?.publisher}
                          <a className='text-blue-500' href={dataSource.url} target='_blank' rel='noopener noreferrer'>
                            <svg
                              className='w-4 h-4 inline ml-1'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                              />
                            </svg>
                          </a>
                        </span>
                      ) : (
                        <span>{dataSource?.publisher} N/A</span>
                      )}
                    </div>
                  ))
                ) : (
                  <span>N/A</span>
                )}
              </>
            ),
          },
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      />

      <SanctionsioScreeningCard
        disabled={readOnly}
        questionNumber={qnCounter++}
        handleSubmitComment={handleSubmitNestedArrayComment}
        result={formData}
        inputName={'sanctionsHits'}
        handleInputChange={handleNestedArrayInputChange}
        question={'Were there any hits for sanctions?'}
        onOpenDetail={openDetailDrawer}
        apiFailed={apiFailed}
        tableHeaders={[
          {
            displayTitle: 'Name',
            width: 'w-3/12',
            dataField: result => (
              <div className='relative flex items-center space-x-2 group'>
                <button
                  className='text-blue-500 hover:text-blue-700 mr-1'
                  onClick={e => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title='View details'
                >
                  ℹ️
                </button>
                <span>{result.name}</span>
                {result?.matched_rca && (
                  <div className='relative'>
                    <span className='inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded'>
                      RCA
                    </span>
                    <div className='absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64'>
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          {
            displayTitle: 'Data Source',
            width: 'w-1.5/12',
            dataField: result => (
              <>
                {result?.dataSources?.length > 0 ? (
                  (() => {
                    const firstDataSource = result.dataSources[0];
                    return (
                      <div className='space-y-1'>
                        {firstDataSource?.country?.[0] && (
                          <div className='text-sm'>
                            <span className='font-medium'>Country: </span>
                            {firstDataSource.country[0]}
                          </div>
                        )}
                        {firstDataSource?.reason?.[0] && (
                          <div className='text-sm'>
                            <span className='font-medium'>Reason: </span>
                            {firstDataSource.reason[0]}
                          </div>
                        )}
                        {firstDataSource?.sanction_type?.[0] && (
                          <div className='text-sm'>
                            <span className='font-medium'>Type: </span>
                            {firstDataSource.sanction_type[0]}
                          </div>
                        )}
                        {firstDataSource?.sanctioning_authority?.[0] && (
                          <div className='text-sm'>
                            <span className='font-medium'>Authority: </span>
                            {firstDataSource.sanctioning_authority[0]}
                          </div>
                        )}
                        {(firstDataSource?.sanction_source?.[0] || firstDataSource?.reference_link?.[0]) && (
                          <div className='text-sm'>
                            <span className='font-medium'>Reference: </span>
                            <a
                              className='text-blue-500'
                              href={firstDataSource.sanction_source?.[0] || firstDataSource.reference_link?.[0]}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              <svg
                                className='w-4 h-4 inline ml-1'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                />
                              </svg>
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <span>N/A</span>
                )}
              </>
            ),
          },
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      />

      <SanctionsioScreeningCard
        disabled={readOnly}
        questionNumber={qnCounter++}
        handleSubmitComment={handleSubmitNestedArrayComment}
        result={formData}
        inputName={'adverseNewsHits'}
        handleInputChange={handleNestedArrayInputChange}
        question={'Are there any adverse news?'}
        onOpenDetail={openDetailDrawer}
        apiFailed={apiFailed}
        tableHeaders={[
          {
            displayTitle: 'Title',
            width: 'w-2.5/12',
            dataField: result => (
              <div className='relative flex items-center space-x-2 group'>
                <button
                  className='text-blue-500 hover:text-blue-700 mr-1'
                  onClick={e => {
                    e.stopPropagation();
                    if (result?.summary) openDetailDrawer(result.summary);
                  }}
                  title='View details'
                >
                  ℹ️
                </button>
                <span>{result.title}</span>
                {result?.matched_rca && (
                  <div className='relative'>
                    <span className='inline-block bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded'>
                      RCA
                    </span>
                    <div className='absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-full mt-1 z-10 w-64'>
                      {rcaTooltip}
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          {
            displayTitle: 'Description',
            width: 'w-3.5/12',
            dataField: result => (
              <>
                {result.description}
                {result?.url ? (
                  <a className='text-blue-500' href={result?.url} target='_blank' rel='noopener noreferrer'>
                    <svg
                      className='w-4 h-4 inline ml-1'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                      />
                    </svg>
                  </a>
                ) : (
                  <span>N/A</span>
                )}
              </>
            ),
          },
          { displayTitle: 'True Hit?', width: 'w-1/12', dataField: null },
          { displayTitle: 'Justification', width: 'w-5/12', dataField: null },
        ]}
      />
    </div>
  );
};

export default SanctionsScreeningSection;
